// Reconciliation (SCR-013): Bedrock drift and metering completeness for one customer (API-024,
// API-025). The customer picker jumps to `/reconciliation/:orgId`; the month range becomes the
// reports' whole-UTC-day window (`from` inclusive, `to` exclusive, at most 92 days on the API, so
// at most 3 months here). Pure; tests/unit/reconciliation.test.ts.
import type { components } from "../../api/schema";
import { buildPath } from "../../router/router";
import { addMonths, currentMonth, lastMonths, type MonthRange } from "@nexus/ui";

export type BedrockReconciliation = components["schemas"]["BedrockReconciliation"];
export type BedrockReconciliationItem = components["schemas"]["BedrockReconciliationItem"];
export type BedrockReconciliationFinding = components["schemas"]["BedrockReconciliationFinding"];
export type Completeness = components["schemas"]["Completeness"];
export type CompletenessItem = components["schemas"]["CompletenessItem"];

export const RECONCILIATION_INDEX_PATH = "/reconciliation";
export const RECONCILIATION_ORG_PATH = "/reconciliation/:orgId" as const;

export interface ReconciliationFilters {
  from: string;
  to: string;
  installationId: string;
  model: string;
}

export const EMPTY_FILTERS: ReconciliationFilters = { from: "", to: "", installationId: "", model: "" };

/** Query parameters for `getBedrockReconciliation`, omitting blank filters. */
export function bedrockQuery(filters: ReconciliationFilters): { from?: string; to?: string; installationId?: string; model?: string } {
  const query: { from?: string; to?: string; installationId?: string; model?: string } = {};
  const from = filters.from.trim();
  const to = filters.to.trim();
  const installationId = filters.installationId.trim();
  const model = filters.model.trim();
  if (from !== "") query.from = from;
  if (to !== "") query.to = to;
  if (installationId !== "") query.installationId = installationId;
  if (model !== "") query.model = model;
  return query;
}

/** Query parameters for `getCompleteness` (it has no `model` filter). */
export function completenessQuery(filters: ReconciliationFilters): { from?: string; to?: string; installationId?: string } {
  const { model: _model, ...rest } = bedrockQuery(filters);
  return rest;
}

const FINDING_LABELS: Record<BedrockReconciliationFinding, string> = {
  bedrock_missing: "Bedrock reported nothing for this cell",
  bedrock_zero: "Bedrock reported zero",
  cache_read_unreported: "AWS publishes no cache-read metric for this model",
  cache_write_unreported: "AWS publishes no cache-write metric for this model",
  shared_account: "Another installation shares this AWS account",
  account_mismatch: "The report named a different AWS account than the one registered",
};

export interface DriftRowView {
  item: BedrockReconciliationItem;
  overThreshold: boolean;
  findingLabels: readonly string[];
}

export function driftRowView(item: BedrockReconciliationItem): DriftRowView {
  return { item, overThreshold: item.overThreshold, findingLabels: item.findings.map((finding) => FINDING_LABELS[finding]) };
}

export function driftRows(reconciliation: BedrockReconciliation): DriftRowView[] {
  return reconciliation.items.map(driftRowView);
}

export interface CompletenessRowView {
  item: CompletenessItem;
  /** SCR-013 "over-threshold rows labelled in text": incomplete counters, or under 100% ingested. */
  incomplete: boolean;
}

export function completenessRowView(item: CompletenessItem): CompletenessRowView {
  const incomplete = !item.countersComplete || (item.completenessBp !== null && item.completenessBp < 10_000);
  return { item, incomplete };
}

export function completenessRows(completeness: Completeness): CompletenessRowView[] {
  return completeness.items.map(completenessRowView);
}

/** Where the org picker sends the operator, or `null` for a blank input. */
export function targetOrgPath(input: string): string | null {
  const orgId = input.trim();
  return orgId === "" ? null : buildPath(RECONCILIATION_ORG_PATH, { orgId });
}

/** The longest month range the reports accept (the drift report refuses a window over 92 days). */
export const MAX_RANGE_MONTHS = 3;

/** The default: last month and this one. */
export function defaultRange(now: Date): MonthRange {
  return lastMonths(2, currentMonth(now));
}

/** The reports' filters for a month range: its first day up to the first day after it (exclusive). */
export function rangeFilters(range: MonthRange, rest: Pick<ReconciliationFilters, "installationId" | "model"> = { installationId: "", model: "" }): ReconciliationFilters {
  return { from: `${range.from}-01`, to: `${addMonths(range.to, 1)}-01`, ...rest };
}

/** Basis points as a signed percentage: 120 → "+1.2%", −325 → "−3.3%", 0 → "0.0%", null → "—". */
export function signedPercent(bp: number | null | undefined): string {
  if (bp === null || bp === undefined) return "—";
  const value = bp / 100;
  const text = Math.abs(value).toFixed(1);
  if (Number(text) === 0) return "0.0%";
  return `${value < 0 ? "−" : "+"}${text}%`;
}

/** Completeness in basis points as a percentage: 9990 → "99.9%"; null → "—". */
export function completenessPercent(bp: number | null): string {
  if (bp === null) return "—";
  return `${(Math.trunc(bp / 10) / 10).toFixed(1)}%`;
}

/** An installation's name when known, else the start of its id. */
export function installationLabel(names: ReadonlyMap<string, string>, id: string): string {
  return names.get(id) ?? `Installation ${id.slice(0, 8)}`;
}

/** A count, or "—" for a figure the installation did not report. */
export function countOrDash(value: number | null): string {
  return value === null ? "—" : value.toLocaleString("en-US");
}
